// src/components/RichiestaFatturazioneModal.js
import React, { useState, useEffect } from "react";
import { collection, query, where, onSnapshot, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { prossimoProgressivo } from "../utils/contatoriUtils";
import { useToast } from "../utils/Toast";
import { messaggioErrore } from "../utils/erroriUtente";
import { X, FileText } from "lucide-react";
import { toLocalDateStr } from "../utils/dateUtils";
import { numIt } from "../utils/economiaCommessa";
import { useVedeTutteFatturazioni } from "../hooks/useCanSee";
import { riepilogoRichieste, creaRichiestaFatturazione, sincronizzaFatturazioneCommessa } from "../utils/fatturazioneCommessa";
import { avvisaChiFattura } from "../utils/avvisiFatturazione";

function formatEuro(n) {
  return new Intl.NumberFormat("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
}

export default function RichiestaFatturazioneModal({ project, orgId, currentUser, onClose }) {
  const { addToast } = useToast();
  const vedeTutte = useVedeTutteFatturazioni(); // null = permessi in caricamento
  const [budget, setBudget] = useState(undefined); // undefined = in caricamento, null = assente
  const [richiesteVisibili, setRichiesteVisibili] = useState([]);
  const [tipo, setTipo] = useState(null); // null | "totale" | "parziale"
  const [percentualeInput, setPercentualeInput] = useState("");
  const [note, setNote] = useState("");
  const [salvando, setSalvando] = useState(false);

  // Chi vede tutte le richieste riallinea prima i totali della commessa (dati vecchi o
  // scritti da un PM che vedeva solo le sue)
  useEffect(() => {
    if (vedeTutte !== true || !project?.id || !orgId) return;
    sincronizzaFatturazioneCommessa(project.id, orgId).catch(err => console.warn("Totali fatturazione non riallineati:", err));
  }, [vedeTutte, project?.id, orgId]);

  useEffect(() => {
    if (!project?.id || !orgId || vedeTutte === null) return;
    const qBudget = query(collection(db, "budgetCommessa"), where("projectId", "==", project.id), where("organizationId","==",orgId));
    const unsub1 = onSnapshot(qBudget, snap => setBudget(snap.empty ? null : snap.docs[0].data()), () => setBudget(null));
    // Le richieste della commessa: tutte per chi le vede tutte, le proprie per il PM (regole 57)
    const filtri = [where("projectId", "==", project.id), where("organizationId","==",orgId)];
    if (!vedeTutte) filtri.push(where("richiedenteUid", "==", currentUser.uid));
    const unsub2 = onSnapshot(query(collection(db, "richiesteFatturazione"), ...filtri),
      snap => setRichiesteVisibili(snap.docs.map(d => ({ id: d.id, ...d.data() }))), () => setRichiesteVisibili([]));
    return () => { unsub1(); unsub2(); };
  }, [project?.id, orgId, vedeTutte, currentUser?.uid]);

  const importoCommessa = budget && numIt(budget.importoCommessa) > 0 ? numIt(budget.importoCommessa) : null;
  // Percentuale già richiesta sulla COMMESSA (di chiunque): dai totali salvati sul budget;
  // se non ci sono ancora, da quelle che si vedono. Chi vede tutto usa sempre le richieste.
  const totaliVisibili = riepilogoRichieste(richiesteVisibili);
  const salvati = budget?.fatturazioneCalcolata || null;
  const giaFatturato = vedeTutte ? totaliVisibili.percRichiesta : (salvati ? salvati.percRichiesta || 0 : totaliVisibili.percRichiesta);
  const totaliIncerti = !vedeTutte && (!salvati || salvati.parziale);
  const residuo = Math.max(0, 100 - giaFatturato);

  const percentualeRichiesta = tipo === "totale" ? residuo : parseFloat(percentualeInput) || 0;
  const superaLimite = percentualeRichiesta > residuo + 0.001;
  const importoRichiesto = importoCommessa !== null ? (importoCommessa * percentualeRichiesta) / 100 : null;
  const nuovaPercentualeComplessiva = giaFatturato + percentualeRichiesta;
  const nuovaResidua = Math.max(0, 100 - nuovaPercentualeComplessiva);

  const pronto = vedeTutte !== null && budget !== undefined;
  const puoConfermare = pronto && tipo && percentualeRichiesta > 0 && !superaLimite;

  const conferma = async () => {
    if (!puoConfermare) return;
    setSalvando(true);
    try {
      const anno = new Date().getFullYear();
      const progressivo = await prossimoProgressivo(`${orgId}_fatturazione_${anno}`, { orgId, anno });
      const numero = `FT-${anno}-${String(progressivo).padStart(4, "0")}`;
      const chi = currentUser.displayName || currentUser.email || "";

      await creaRichiestaFatturazione({
        projectId: project.id, orgId, completi: !!vedeTutte,
        totaliVisibili: vedeTutte ? totaliVisibili : (salvati || totaliVisibili),
        dati: {
          organizationId: orgId,
          numero,
          projectId: project.id,
          projectName: project.name || "",
          codiceCommessa: project.codiceCommessa || "",
          numeroOrdine: project.numeroOrdine || "",
          clienteId: project.clienteId || "",
          clienteNome: project.clienteNome || "",
          responsabileNome: project.responsabileNome || "",
          responsabileUid: project.responsabileUid || "",
          tipo,
          percentuale: percentualeRichiesta,
          percentualeComplessiva: nuovaPercentualeComplessiva,
          percentualeResidua: nuovaResidua,
          importoRichiesto: importoRichiesto,
          importoCommessaAlMomento: importoCommessa,
          note,
          stato: "da_fatturare",
          dataRichiesta: toLocalDateStr(new Date()),
          richiedenteUid: currentUser.uid,
          richiedenteNome: chi,
          createdAt: serverTimestamp(),
        },
      });
      avvisaChiFattura({ orgId, mittenteUid: currentUser.uid,
        titolo: "Nuova richiesta di fatturazione",
        testo: `${chi || "Un PM"} chiede di fatturare ${numero} · ${project.codiceCommessa ? `[${project.codiceCommessa}] ` : ""}${project.name || ""}${importoRichiesto !== null ? ` · € ${formatEuro(importoRichiesto)}` : ""}` });

      addToast("Richiesta di fatturazione inviata all'amministrazione ✅", "success");
      onClose();
    } catch (err) {
      addToast(err?.code === "nexiora/oltre-100" ? err.message : messaggioErrore(err), "error");
    }
    setSalvando(false);
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 440 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, display: "flex", alignItems: "center", gap: 8 }}>
            <FileText size={15} color="#7c3aed" /> Richiesta di fatturazione
          </h3>
          <button className="btn-icon" onClick={onClose}><X size={16} /></button>
        </div>
        <p style={{ fontSize: 12, color: "#9ca3af", marginBottom: 16 }}>
          {project?.codiceCommessa ? `[${project.codiceCommessa}] ` : ""}{project?.name}
        </p>

        {importoCommessa === null && (
          <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, padding: "10px 12px", marginBottom: 14, fontSize: 12, color: "#92400e" }}>
            ⚠️ Non sono stati trovati dati economici della Commessa. La richiesta verrà comunque inviata al reparto Amministrativo. Sarà necessario verificare manualmente l'importo da fatturare.
          </div>
        )}

        {!pronto && <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 14 }}>Caricamento…</div>}

        {pronto && totaliIncerti && richiesteVisibili.length > 0 && (
          <div style={{ fontSize: 11.5, color: "#92400e", marginBottom: 10, background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, padding: "8px 12px" }}>
            La percentuale già richiesta è calcolata sulle tue richieste: l'amministrazione verifica l'avanzamento complessivo della commessa prima di fatturare.
          </div>
        )}

        {pronto && giaFatturato > 0 && (
          <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 14, background: "#f9fafb", borderRadius: 8, padding: "8px 12px" }}>
            Già richiesto in fatturazione: <strong>{(giaFatturato).toLocaleString("it-IT",{minimumFractionDigits:1,maximumFractionDigits:1})}%</strong> · Residuo disponibile: <strong>{(residuo).toLocaleString("it-IT",{minimumFractionDigits:1,maximumFractionDigits:1})}%</strong>
          </div>
        )}

        {!pronto ? null : residuo <= 0 ? (
          <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "10px 12px", fontSize: 13, color: "#991b1b", fontWeight: 600 }}>
            Sulla commessa è già stato richiesto in fattura il 100%. Nessun residuo disponibile per nuove richieste.
          </div>
        ) : (
          <>
            <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
              <button onClick={() => setTipo("totale")}
                style={{ flex: 1, padding: "12px 8px", borderRadius: 10, border: `2px solid ${tipo === "totale" ? "#7c3aed" : "#e5e7eb"}`, background: tipo === "totale" ? "#faf5ff" : "#fff", cursor: "pointer", textAlign: "center" }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>Fatturazione Totale</div>
                <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>Richiede il residuo ({(residuo).toLocaleString("it-IT",{minimumFractionDigits:1,maximumFractionDigits:1})}%)</div>
              </button>
              <button onClick={() => setTipo("parziale")}
                style={{ flex: 1, padding: "12px 8px", borderRadius: 10, border: `2px solid ${tipo === "parziale" ? "#7c3aed" : "#e5e7eb"}`, background: tipo === "parziale" ? "#faf5ff" : "#fff", cursor: "pointer", textAlign: "center" }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>Fatturazione Parziale</div>
                <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>Scegli tu la percentuale</div>
              </button>
            </div>

            {tipo === "parziale" && (
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", display: "block", marginBottom: 4 }}>Percentuale da fatturare (%)</label>
                <input type="number" min="0.1" max={residuo} step="0.5" value={percentualeInput}
                  onChange={e => setPercentualeInput(e.target.value)}
                  style={{ width: "100%", padding: "8px 10px", border: `1px solid ${superaLimite ? "#dc2626" : "#e5e7eb"}`, borderRadius: 8, fontSize: 14, fontFamily: "'Inter',sans-serif", outline: "none" }}
                  placeholder="Es. 25" />
                {superaLimite && (
                  <div style={{ fontSize: 11, color: "#dc2626", marginTop: 4, fontWeight: 600 }}>
                    Impossibile procedere. La percentuale complessiva della Commessa supererebbe il 100%.
                  </div>
                )}
              </div>
            )}

            {tipo && !superaLimite && percentualeRichiesta > 0 && (
              <div style={{ background: "#faf5ff", border: "1px solid #e9d5ff", borderRadius: 10, padding: 14, marginBottom: 14 }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 6 }}>
                  <span style={{ color: "#6b7280" }}>Importo contrattuale</span>
                  <strong>{importoCommessa !== null ? `€ ${formatEuro(importoCommessa)}` : "Da verificare"}</strong>
                </div>
                {/* W452: la percentuale vale sul contratto; le varianti si fatturano dalla loro scheda */}
                <div style={{ fontSize: 10.5, color: "#9ca3af", marginTop: -2, marginBottom: 6 }}>Varianti escluse: si fatturano dalla scheda Varianti.</div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 6 }}>
                  <span style={{ color: "#6b7280" }}>Importo da fatturare ({(percentualeRichiesta).toLocaleString("it-IT",{minimumFractionDigits:1,maximumFractionDigits:1})}%)</span>
                  <strong style={{ color: "#7c3aed" }}>{importoRichiesto !== null ? `€ ${formatEuro(importoRichiesto)}` : "Da verificare"}</strong>
                </div>
                <div style={{ borderTop: "1px solid #e9d5ff", marginTop: 8, paddingTop: 8, display: "flex", justifyContent: "space-between", fontSize: 11, color: "#9ca3af" }}>
                  <span>Richiesto in fattura dopo questa richiesta</span>
                  <span>{(nuovaPercentualeComplessiva).toLocaleString("it-IT",{minimumFractionDigits:1,maximumFractionDigits:1})}% · residuo {(nuovaResidua).toLocaleString("it-IT",{minimumFractionDigits:1,maximumFractionDigits:1})}%</span>
                </div>
              </div>
            )}

            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", display: "block", marginBottom: 4 }}>Note per l'amministrazione (facoltative)</label>
              <textarea value={note} onChange={e => setNote(e.target.value)} rows={2}
                style={{ width: "100%", padding: "8px 10px", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 13, fontFamily: "'Inter',sans-serif", outline: "none", resize: "vertical" }} />
            </div>

            <button className="btn btn-primary" style={{ width: "100%" }} disabled={!puoConfermare || salvando} onClick={conferma}>
              {salvando ? "Invio..." : "📄 Invia richiesta di fatturazione"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
